import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faClipboard,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import { Row, Col } from "react-bootstrap";
import { fetchAllReservations } from "../actions/restaurantActions";

import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import CollapsibleChat from "./chat/CollapsibleChat";
import ReservationCard from "./ReservationCard";

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const BarContainer = styled.div`
  width: 100vw;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.2);

  animation: ${({ $clicked }) => ($clicked ? slideIn : slideOut)} 0.5s forwards;
`;

const InsideContainerBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ee754c;
  overflow: hidden;
  display: flex;
  justify-content: end;
`;

const InsideContainer = styled.div`
  padding: 30px 5px 5px 10px;
  position: relative;
  width: 98%;
  background-color: white;
  height: 100%;
  overflow: hidden;

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #e20202ff;
    &:hover {
      background-color: #bbbbbb;
    }
  }

  &::-webkit-scrollbar {
    width: 12px;
    background-color: transparent;
  }

  @media (max-width: 768px) {
    padding-top: 10px;
    padding-left: 15px;
    padding-right: 15px;
  }

  @media (max-width: 480px) {
    padding-top: 50px;
  }
`;

const orbit = (start, end) => keyframes`
  from { transform: rotate(${start}deg); }
  to { transform: rotate(${end}deg); }
`;

const CircleContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: fixed;
  top: 15px;
  right: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #98afad;
  cursor: pointer;
  padding: 2px;
  z-index: 1001;

  @media (max-width: 768px) {
    top: 10px;
    right: 50px;
  }
`;

const NotificationCircle = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  bottom: 20px;
  right: 20px;

  & > div {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  ${({ $animationDegrees, $animate }) =>
    $animate &&
    css`
      transform-origin: 29px 27px;
      animation: ${orbit(
          $animationDegrees?.start ?? 0,
          $animationDegrees?.end ?? 0,
        )}
        0.5s forwards;
    `}
`;

const OrangeNotification = styled(NotificationCircle)`
  color: white;
  background: #ee754c;
`;

const RedCircle = styled(NotificationCircle)`
  color: white;
  background: red;
`;

const tremble = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(-6deg); }
  75% { transform: rotate(6deg); }
  100% { transform: rotate(0deg); }
`;

const AnimatedIcon = styled(FontAwesomeIcon)`
  color: #5fddb3;
  font-size: 60px;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${tremble} 0.2s ease-in-out 3;
    `}
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 10px;
  height: 10px;
  background-color: #ee754c;
  border-radius: 50%;
  border: 1px solid white;
`;

function NotificationBell({
  restaurant_id,
  allReservations,
  reservationsWithChatMessages,
  reservations_past_estimated_time,
}) {
  const [activeKey, setActiveKey] = useState("reservas");

  const userInfoState = useSelector((state) => state.userLogin);
  const { userInfo } = userInfoState;

  const notificationBellRef = useRef(null);
  const colRef = useRef(null);
  const contentRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);

  const dispatch = useDispatch();
  const [clicked, setClicked] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [pendingNumber, setPendingNumber] = useState(0);
  const [cancelledNumber, setCancelledNumber] = useState(0);

  const [
    allReservationsWithDateSeparators,
    setAllReservationsWithDateSeparators,
  ] = useState([]);

  const [hasUnreadChatNotifications, setHasUnreadChatNotifications] =
    useState(false);
  const [hasNewReservation, setHasNewReservation] = useState(false);

  useEffect(() => {
    if (allReservations) {
      const hasUnread = allReservations.some((reservation) => {
        const notifications =
          typeof reservation.chat_notification_restaurant === "string"
            ? reservation.chat_notification_restaurant.split(",")
            : [];

        return (
          (reservation.chat_notification_restaurant_all ||
            (reservation.chat_notification_restaurant &&
              notifications.includes(userInfo?.name))) &&
          reservation.chat_has_messages
        );
      });

      setHasUnreadChatNotifications(hasUnread);

      const hasNew = allReservations.some(
        (reservation) =>
          reservation.status === "pending_restaurant" ||
          reservation.status === "Edited" ||
          reservation.status === "Waiting_List" ||
          reservation.cancelled_warning === true,
      );

      setHasNewReservation(hasNew);
    }
  }, [allReservations, userInfo]);

  const handleScrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleTabSelect = (key) => {
    setActiveKey(key);
    handleScrollToTop();
  };

  const handleChatToggle = () => {};

  const stableRestaurantId = useMemo(() => restaurant_id, [restaurant_id]);

  useEffect(() => {
    if (stableRestaurantId) {
      dispatch(
        fetchAllReservations(
          stableRestaurantId,
          null,
          null,
          null,
          "all_pending_reservations_from_all_time",
        ),
      );
    }
  }, [dispatch, stableRestaurantId]);

  const PENDING_STATUSES = new Set([
    "pending_restaurant",
    "Edited",
    "Waiting_List",
  ]);

  const [freeTablesReservations, setFreeTablesReservations] = useState([]);
  const [pendingReservations, setPendingReservations] = useState([]);

  useEffect(() => {
    setFreeTablesReservations(
      reservations_past_estimated_time?.filter(
        (r) => String(r.status).toLowerCase() === "attended",
      ) ?? [],
    );

    setPendingReservations(
      allReservations?.filter(
        (r) => PENDING_STATUSES.has(r.status) || !!r.cancelled_warning,
      ) ?? [],
    );
  }, [reservations_past_estimated_time, allReservations]);

  useEffect(() => {
    if (!allReservations?.length) {
      const freeTablesCount =
        reservations_past_estimated_time?.filter(
          (r) => String(r.status).toLowerCase() === "attended",
        ).length ?? 0;

      setPendingNumber(freeTablesCount);
      return;
    }

    const pendingBaseCount = allReservations.filter(
      (r) =>
        r.cancelled_warning === true ||
        r.status === "Cancelled_By_Customer_Warning" ||
        r.status === "pending_restaurant" ||
        r.status === "new_chat_message" ||
        r.status === "Edited" ||
        r.chat_notification_restaurant_all,
    ).length;

    const freeTablesCount =
      reservations_past_estimated_time?.filter(
        (r) => String(r.status).toLowerCase() === "attended",
      ).length ?? 0;

    setPendingNumber(pendingBaseCount + freeTablesCount);
  }, [allReservations, reservations_past_estimated_time]);

  useEffect(() => {
    if (allReservations && allReservations.length > 0) {
      const sortedReservations = [...allReservations].sort((a, b) =>
        a.date.localeCompare(b.date),
      );

      const reservationsWithSeparators = [];
      let lastDate = null;

      sortedReservations.forEach((reservation) => {
        if (reservation.date !== lastDate) {
          reservationsWithSeparators.push({
            isDateSeparator: true,
            date: reservation.date,
          });
          lastDate = reservation.date;
        }
        reservationsWithSeparators.push(reservation);
      });

      setAllReservationsWithDateSeparators(reservationsWithSeparators);
    }
  }, [allReservations]);

  const animationDegreesYellow = clicked
    ? { start: 180, end: 0 }
    : { start: 0, end: 180 };

  const animationDegreesRed = clicked
    ? { start: 140, end: -40 }
    : { start: -40, end: 140 };

  const counterRotate = clicked ? "rotate(0deg)" : "rotate(-180deg)";
  const counterRotateRed = clicked ? "rotate(40deg)" : "rotate(-140deg)";

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();

    inactivityTimeoutRef.current = setTimeout(
      () => {
        setClicked(false);
      },
      3 * 60 * 1000,
    );
  }, [clearInactivityTimer]);

  const hasOtherModalOpen = useCallback(() => {
    return !!document.querySelector(".modal.show");
  }, []);

  useEffect(() => {
    if (!clicked) {
      clearInactivityTimer();
      return;
    }

    resetInactivityTimer();

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
    ];

    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      clearInactivityTimer();
    };
  }, [clicked, resetInactivityTimer, clearInactivityTimer]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!clicked) return;

      if (
        notificationBellRef.current &&
        notificationBellRef.current.contains(event.target)
      ) {
        return;
      }

      if (colRef.current && colRef.current.contains(event.target)) {
        return;
      }

      if (hasOtherModalOpen()) {
        return;
      }

      setClicked(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [clicked, hasOtherModalOpen]);

  const handleClick = () => {
    setAnimate(true);
    setClicked((prev) => !prev);

    setTimeout(() => {
      setAnimate(false);
    }, 200);
  };

  return (
    <>
      <CircleContainer ref={notificationBellRef} onClick={handleClick}>
        <AnimatedIcon
          $animate={animate}
          icon={faBell}
          style={{ color: "#ffffff", fontSize: "30px" }}
        />

        {pendingNumber > 0 && (
          <OrangeNotification
            $animate={true}
            $animationDegrees={animationDegreesYellow}
          >
            <div style={{ transform: counterRotate }}>
              {pendingNumber < 10 ? pendingNumber : "9+"}
            </div>
          </OrangeNotification>
        )}

        {cancelledNumber > 0 && (
          <RedCircle $animate={true} $animationDegrees={animationDegreesRed}>
            <div style={{ transform: counterRotateRed }}>
              {cancelledNumber < 10 ? cancelledNumber : "9+"}
            </div>
          </RedCircle>
        )}
      </CircleContainer>

      <BarContainer $clicked={clicked}>
        <Row
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "end",
            padding: 0,
            margin: "0",
          }}
        >
          <Col
            ref={colRef}
            xs={12}
            sm={12}
            md={6}
            xl={4}
            style={{
              color: "black",
              height: "100%",
              padding: 0,
              margin: "0",
            }}
          >
            <InsideContainerBox>
              <div className="menu-left"></div>
              <InsideContainer>
                <div style={{ position: "relative", height: "100%" }}>
                  <Tabs
                    activeKey={activeKey}
                    onSelect={handleTabSelect}
                    defaultActiveKey="reservas"
                    id="uncontrolled-tab-example"
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1000,
                    }}
                  >
                    <Tab
                      eventKey="reservas"
                      title={
                        <>
                          <b style={{ color: "inherit" }}>Reservas</b>
                          <FontAwesomeIcon
                            icon={faClipboard}
                            style={{ marginLeft: "5px", color: "inherit" }}
                          />
                          {hasNewReservation && <NotificationDot />}
                        </>
                      }
                    />
                    <Tab
                      eventKey="mensagens"
                      title={
                        <>
                          <b style={{ color: "inherit" }}>Mensagens</b>
                          <FontAwesomeIcon
                            icon={faComments}
                            style={{ marginLeft: "5px", color: "inherit" }}
                          />
                          {hasUnreadChatNotifications && <NotificationDot />}
                        </>
                      }
                    />
                  </Tabs>

                  <div
                    ref={contentRef}
                    style={{
                      position: "absolute",
                      top: "65px",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      width: "100%",
                      overflowY: "auto",
                      overflowBehavior: "contain",
                    }}
                  >
                    {activeKey === "reservas" ? (
                      <div
                        style={{ overflowBehavior: "contain", width: "100%" }}
                      >
                        {pendingReservations.length > 0 && (
                          <ReservationCard
                            style={{ width: "100%" }}
                            reservations={pendingReservations}
                            isNotifications={true}
                          />
                        )}

                        {freeTablesReservations.length > 0 && (
                          <ReservationCard
                            style={{ width: "100%" }}
                            reservations={freeTablesReservations}
                            past_estimated_time={true}
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        style={{ padding: "15px", overflowBehavior: "contain" }}
                      >
                        {allReservations &&
                          allReservations
                            .filter((reservation) => {
                              const notifications =
                                typeof reservation.chat_notification_restaurant ===
                                "string"
                                  ? reservation.chat_notification_restaurant.split(
                                      ",",
                                    )
                                  : [];

                              return (
                                (reservation.chat_notification_restaurant_all ||
                                  (reservation.chat_notification_restaurant &&
                                    notifications.includes(userInfo?.name))) &&
                                reservation.chat_has_messages
                              );
                            })
                            .map((reservation) => (
                              <CollapsibleChat
                                key={reservation.id}
                                reservation={reservation}
                                isClient={false}
                                style={{ width: "100%" }}
                                isNewMessage={true}
                                onToggle={handleChatToggle}
                              />
                            ))}

                        {reservationsWithChatMessages &&
                          reservationsWithChatMessages
                            .filter((reservation) => {
                              const notifications =
                                typeof reservation.chat_notification_restaurant ===
                                "string"
                                  ? reservation.chat_notification_restaurant.split(
                                      ",",
                                    )
                                  : [];

                              return (
                                !reservation.chat_notification_restaurant_all &&
                                (!reservation.chat_notification_restaurant ||
                                  !notifications.includes(userInfo?.name)) &&
                                reservation.chat_has_messages
                              );
                            })
                            .map((reservation) => (
                              <CollapsibleChat
                                key={reservation.id}
                                reservation={reservation}
                                isClient={false}
                                style={{ width: "100%" }}
                                isNewMessage={false}
                                onToggle={handleChatToggle}
                              />
                            ))}
                      </div>
                    )}
                  </div>
                </div>
              </InsideContainer>
            </InsideContainerBox>
          </Col>
        </Row>
      </BarContainer>
    </>
  );
}

export default NotificationBell;
